import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import EditIcon from '@material-ui/icons/EditOutlined';
import HomeWork from '@material-ui/icons/HomeWorkOutlined';
import { useParams } from 'react-router';
import { RootState } from '../../redux/root';
import { Loader } from '../../atoms';
import ContactTypeSelect from './ContactTypeSelect';
import { updateUsersAccountingFlag, updateUsersInspectionFlag } from '../../api/users';
import { setPermitContacts } from '../../redux/slices/projectSlice';
import ModalInfo from '../../molecules/ModalInfo';
import { CLIENT_TYPES_VALUES } from '../../constants/forms';

const getBooleanValue = value => value === true || value === 1 || value === 'true';

const ContactsTable = ({ dataRows, handleEdit, setShowUserPermitsModal, manuallyUpdate = 0 }) => {
  const dispatch = useDispatch();
  // @ts-ignore
  const { permitId } = useParams();
  const [rows, setRows] = useState([]);
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isUpdating = React.useRef(false);

  const fetchRows = React.useCallback(() => {
    setIsLoading(true);
    return dataRows()
      .then(d => {
        const results = d?.data?.results || [];
        setRows(results);
        dispatch(setPermitContacts(results));
      })
      .finally(() => setIsLoading(false));
  }, [dataRows, dispatch]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manuallyUpdate]);

  const updateInspection = async (userId: number) => {
    isUpdating.current = true;
    const _rows = rows.map(r => {
      const rowInspectionValue = getBooleanValue(r?.isInspection);
      const rowAccountingValue = getBooleanValue(r?.isAccounting);
      return {
        ...r,
        isInspection: r.id === userId ? !rowInspectionValue : rowInspectionValue,
        isAccounting: rowAccountingValue,
      };
    });
    setRows(_rows);
    dispatch(setPermitContacts(_rows));
    const clickedUser = _rows.find(r => r.id === userId);
    const _params = {
      userId,
      permitId,
      userRole: 3,
      isInspection: getBooleanValue(clickedUser?.isInspection),
      isAccounting: getBooleanValue(clickedUser?.isAccounting),
    };
    await updateUsersInspectionFlag(_params);
    isUpdating.current = false;
  };

  const updateContactType = (userId: number, newTypeName: string) => {
    const _rows = rows.map(r =>
      r.id === userId ? { ...r, contactType: newTypeName } : r);
    setRows(_rows);
    dispatch(setPermitContacts(_rows));
  };

  const updateAccounting = async (userId: number) => {
    const user = rows.find(r => r.id === userId);
    if (!user?.email) {
      setShowEmailWarning(true);
      return;
    }
    isUpdating.current = true;
    const _rows = rows.map(r => {
      const rowInspectionValue = getBooleanValue(r?.isInspection);
      return {
        ...r,
        isAccounting: r.id === userId,
        isInspection: rowInspectionValue,
      };
    });
    setRows(_rows);
    dispatch(setPermitContacts(_rows));

    const rowsToClear = rows.filter(r => r.id !== userId && getBooleanValue(r?.isAccounting));
    await Promise.all(
      rowsToClear.map(r =>
        updateUsersAccountingFlag({
          userId: r.id,
          permitId,
          userRole: 3,
          isInspection: getBooleanValue(r?.isInspection),
          isAccounting: false,
        })),
    );

    const clickedUser = _rows.find(r => r.id === userId);
    const _params = {
      userId,
      permitId,
      userRole: 3,
      isInspection: getBooleanValue(clickedUser?.isInspection),
      isAccounting: getBooleanValue(clickedUser?.isAccounting),
    };
    await updateUsersAccountingFlag(_params);
    isUpdating.current = false;
  };

  if (isLoading) {
    return <LoaderWrapper><Loader /></LoaderWrapper>;
  }

  if (!rows.length) {
    return <EmptyState>No contacts found</EmptyState>;
  }

  return (
    <Wrapper>
      <TableCard>
        <TableScroll>
          <Table>
            <colgroup>
              <col />
              <col style={{ width: '200px' }} />
              <col style={{ width: '100px' }} />
              <col style={{ width: '100px' }} />
              <col style={{ width: '88px' }} />
            </colgroup>
            <Thead>
              <tr>
                <Th>Contact</Th>
                <Th>Contact Type</Th>
                <Th $center>Acct</Th>
                <Th $center>Insp</Th>
                <Th $center>Actions</Th>
              </tr>
            </Thead>
            <tbody>
              {rows.map((row, index) => {
                const name = [row.firstName, row.lastName].filter(Boolean).join(' ');
                const rowClientType = (row as any)?.clientType ?? (row as any)?.client_type;
                const isCompany = String(rowClientType) === String(CLIENT_TYPES_VALUES.COMPANY);
                return (
                  <Tr key={index} $zebra={index % 2 === 1}>
                    <Td data-label="Contact">
                      <ContactCell>
                        {row.companyName && (
                          <CompanyName title={row.companyName}>{row.companyName}</CompanyName>
                        )}
                        <ContactName title={name}>{name || '—'}</ContactName>
                        <ContactMeta>
                          {row.email && (
                            <EmailLink href={`mailto:${row.email}`} title={row.email}>
                              {row.email}
                            </EmailLink>
                          )}
                          {row.phoneNumber && (
                            <Phone>{row.phoneNumber}</Phone>
                          )}
                        </ContactMeta>
                      </ContactCell>
                    </Td>
                    <Td data-label="Contact Type">
                      {!isCompany && (
                        <ContactTypeSelect
                          contactType={row.contactType}
                          userId={row.id}
                          onContactTypeChange={updateContactType}
                        />
                      )}
                    </Td>
                    <Td $center data-label="Accounting">
                      <RadioBtn
                        type="radio"
                        name="accounting-contact"
                        checked={getBooleanValue(row.isAccounting)}
                        onChange={() => updateAccounting(row.id)}
                      />
                    </Td>
                    <Td $center data-label="Inspection">
                      <CheckboxBtn
                        type="checkbox"
                        checked={getBooleanValue(row.isInspection)}
                        onChange={() => updateInspection(row.id)}
                      />
                    </Td>
                    <Td $center data-label="Actions">
                      <ActionIcons>
                        <ActionBtn onClick={() => handleEdit(row)} title="Edit">
                          <EditIcon />
                        </ActionBtn>
                        <ActionBtn onClick={() => setShowUserPermitsModal(row.id)} title="View Permits">
                          <HomeWork />
                        </ActionBtn>
                      </ActionIcons>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </TableScroll>
      </TableCard>
      {showEmailWarning && (
        <ModalInfo
          errorMsg="Please add an email address to this contact before selecting for accounting."
          isError
          timeToClose={5000}
          onClose={() => setShowEmailWarning(false)}
        />
      )}
    </Wrapper>
  );
};

export default ContactsTable;

const Wrapper = styled.div`
  width: 100%;
`;

const TableCard = styled.div`
  width: 100%;
  border: 1px solid var(--border, var(--neutral-200));
  border-radius: 10px;
  background: var(--neutral-0);
  box-shadow: var(--shadow-xs);
  overflow: hidden;
  box-sizing: border-box;
`;

const TableScroll = styled.div`
  width: 100%;
  max-height: 70vh;
  overflow-x: hidden;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--fs-sm);
  table-layout: fixed;
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 1;

  @media only screen and (max-width: 840px) {
    display: none;
  }
`;

const Th = styled.th<{ $center?: boolean; $width?: string }>`
  padding: 12px 16px;
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: var(--table-row-header);
  border-bottom: 1px solid var(--border, var(--neutral-200));
  white-space: nowrap;
  ${({ $width }) => $width && `width: ${$width};`}
`;

const Tr = styled.tr<{ $zebra?: boolean }>`
  background: ${({ $zebra }) =>
    ($zebra ? 'var(--table-row-alt)' : 'var(--table-row-base)')};
  transition: background-color var(--motion-fast) var(--ease-standard);

  &:hover {
    background: var(--table-row-hover);
  }

  &:last-child td {
    border-bottom: 0;
  }

  @media only screen and (max-width: 840px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 14px;
    border-bottom: 1px solid var(--neutral-100);

    &:last-child {
      border-bottom: 0;
    }
  }
`;

const Td = styled.td<{ $center?: boolean }>`
  padding: 14px 16px;
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
  border-bottom: 1px solid var(--neutral-100);
  color: var(--neutral-800);
  vertical-align: middle;
  min-width: 0;
  overflow: hidden;

  &[data-label='Contact Type'] {
    padding-left: 8px;
    padding-right: 8px;
  }

  @media only screen and (max-width: 840px) {
    display: block;
    padding: 0;
    border-bottom: 0;
    text-align: left;

    &::before {
      content: attr(data-label);
      display: block;
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--neutral-500);
      margin-bottom: 4px;
    }

    &[data-label='Contact'] {
      grid-column: 1 / -1;
    }
  }
`;

const ContactCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ContactName = styled.div`
  font-weight: 600;
  color: var(--neutral-900);
  font-size: 0.875rem;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompanyName = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContactMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin-top: 2px;
  font-size: 0.8125rem;
  min-width: 0;
`;

const Phone = styled.span`
  color: var(--neutral-600);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const EmailLink = styled.a`
  color: var(--primary-600);
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;

  &:hover {
    color: var(--primary-700);
    text-decoration: underline;
  }
`;

const RadioBtn = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary-600);
  cursor: pointer;
`;

const CheckboxBtn = styled.input`
  width: 16px;
  height: 16px;
  accent-color: var(--primary-600);
  cursor: pointer;
`;

const ActionIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--space-2);
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: var(--radius-md);
  color: var(--neutral-400);
  cursor: pointer;
  transition:
    color var(--motion-fast) var(--ease-standard),
    background-color var(--motion-fast) var(--ease-standard);

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: var(--primary-600);
    background: var(--primary-50);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--space-8) 0;
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--neutral-400);
  font-size: var(--fs-base);
  padding: var(--space-8) 0;
`;
